/**
 * AgentThinkingPanel — renders the ordered step events from a PSI agent (or
 * a chain of agents) for a target the user is watching.
 *
 * Mirrors Core's panel exactly — same visual contract, same DOM structure,
 * same tooltip behaviour. Kept as a near-byte copy so a future shared package
 * extraction is mechanical.
 *
 * Visual contract:
 *   - Calm grey border, white card, no shadow, no gradient, no emoji.
 *   - Step rows render in order; current step has a small spinner; finished
 *     steps have a slim checkmark glyph drawn from CSS, not an emoji.
 *   - Each step has a tooltip-on-hover (200ms delay) that explains the step
 *     in one short sentence. Keyboard accessible (Tab focus -> tooltip).
 *   - Reads like a professional status indicator. No AI personification,
 *     no first-person voice, no exclamations.
 */
import React, { useState } from 'react';

function StatusGlyph({ status }) {
  // Tiny CSS-driven glyphs. No emoji.
  if (status === 'completed') {
    return (
      <span aria-hidden style={{
        display: 'inline-block', width: 14, height: 14, lineHeight: '14px',
        textAlign: 'center', borderRadius: '50%', border: '1px solid #94a3b8',
        color: '#475569', fontSize: 10, fontWeight: 600,
      }}>{'✓'}</span>
    );
  }
  if (status === 'failed') {
    return (
      <span aria-hidden style={{
        display: 'inline-block', width: 14, height: 14, lineHeight: '14px',
        textAlign: 'center', borderRadius: '50%', border: '1px solid #dc2626',
        color: '#dc2626', fontSize: 10, fontWeight: 600,
      }}>{'×'}</span>
    );
  }
  // started — slim spinner via CSS rotation
  return (
    <span aria-hidden style={{
      display: 'inline-block', width: 12, height: 12,
      border: '1.5px solid #cbd5e1', borderTopColor: '#475569',
      borderRadius: '50%', animation: 'agentSpin 0.9s linear infinite',
    }}/>
  );
}

function StepRow({ event }) {
  const [showTip, setShowTip] = useState(false);
  const detail = event.step_detail;
  const interactive = !!detail;

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '6px 4px',
        cursor: interactive ? 'help' : 'default',
        outline: 'none',
      }}
      tabIndex={interactive ? 0 : -1}
      onMouseEnter={() => interactive && setTimeout(() => setShowTip(true), 200)}
      onMouseLeave={() => setShowTip(false)}
      onFocus={() => interactive && setShowTip(true)}
      onBlur={() => setShowTip(false)}
      onKeyDown={(e) => { if (e.key === 'Escape') setShowTip(false); }}
    >
      <StatusGlyph status={event.step_status} />
      <span style={{ fontSize: 13, color: '#1e293b' }}>{event.step_message}</span>
      {event.agent_label && (
        <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>
          {event.agent_label}
        </span>
      )}
      {showTip && detail && (
        <div role="tooltip" style={{
          position: 'absolute', left: 24, top: '100%', marginTop: 4,
          maxWidth: 320, padding: '8px 10px',
          background: '#ffffff', border: '1px solid #cbd5e1',
          fontSize: 12, lineHeight: 1.4, color: '#334155',
          zIndex: 50, opacity: 1, transition: 'opacity 150ms ease',
        }}>
          {detail}
        </div>
      )}
    </div>
  );
}

export default function AgentThinkingPanel({ events, status, title }) {
  if (!events || events.length === 0) return null;

  const displayTitle = title || 'Activity';
  const headerNote =
    status === 'started'  ? 'In progress' :
    status === 'failed'   ? 'Stopped' :
    status === 'completed' ? 'Done' : '';

  return (
    <>
      <style>{`@keyframes agentSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div style={{
        border: '1px solid #cbd5e1', background: '#ffffff',
        padding: '10px 12px', marginTop: 8,
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: 4,
        }}>
          <span style={{
            fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase',
            color: '#64748b', fontFamily: 'ui-monospace, monospace',
          }}>{displayTitle}</span>
          {headerNote && (
            <span style={{ fontSize: 11, color: '#94a3b8' }}>{headerNote}</span>
          )}
        </div>
        {events.map(ev => <StepRow key={ev.id} event={ev} />)}
      </div>
    </>
  );
}
